// src/components/blogs/PublicBlogPostPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import publicAxios from '../../utils/publicAxios';
import { 
  Typography, Box, Container, Paper, Divider, Button, Avatar, Skeleton
} from '@mui/material';
import { motion } from 'framer-motion';
import { getInternalBlogId, PERSONA_DISPLAY_NAMES } from '../../utils/personaUtils';

const PublicBlogPostPage = () => {
  const { blogId: urlBlogId } = useParams();
  const internalBlogId = getInternalBlogId(urlBlogId);
  const navigate = useNavigate();
  
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        setLoading(true);
        // Use public endpoint
        const response = await publicAxios.get(`/public/blog/${internalBlogId}`);
        setBlog(response.data.blog);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog data:', err);
        setError('Failed to load blog content. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchBlogData();
  }, [internalBlogId]);

  // Get blog image 
  const getBlogImage = () => {
    if (!blog) return null;
    
    const blogImageMap = {
      'restoration_artisan_vinatage_dresser_vanity': 'generated_image_dresser_vantity4.png',
      'house_doctor_garage_wall_tool_organization': 'generated_image_modular_garage.png',
      'everyday_woodsmith_slding_barn_door': 'generated_image_sliding_bsrn_kitchen_door.png',
    };
    
    return blogImageMap[urlBlogId] || null;
  };
  
  // Format blog content
  const formatContent = (content) => {
    if (!content) return '';
    
    return content.split('\n\n')
      .filter(para => para.trim() !== '')
      .map((para, index) => (
        <Typography key={index} paragraph>
          {para}
        </Typography>
      ));
  };
  
  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Skeleton variant="text" height={60} width="80%" sx={{ mb: 1 }} />
          <Skeleton variant="text" height={30} width="40%" sx={{ mb: 4 }} />
          <Skeleton variant="text" height={20} width="100%" />
          <Skeleton variant="text" height={20} width="100%" />
          <Skeleton variant="text" height={20} width="95%" />
          <Skeleton variant="text" height={20} width="98%" />
          <Skeleton variant="text" height={20} width="60%" sx={{ mb: 2 }} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 6,
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Return to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog?.title || 'DIY Article'} | DIYMind</title>
        <meta name="description" content={blog?.content?.substring(0, 160) || 'DIY expert advice and guides'} />
        <link rel="canonical" href={`https://diymind.ai/public/blog/${urlBlogId}`} />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              "headline": "${blog?.title || ''}",
              "image": "${getBlogImage() ? `https://diymind.ai/${getBlogImage()}` : ''}",
              "author": {
                "@type": "Person",
                "name": "${PERSONA_DISPLAY_NAMES[blog?.persona_id] || ''}"
              },
              "publisher": {
                "@type": "Organization",
                "name": "DIYMind",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://diymind.ai/logo.png"
                }
              }
            }
          `}
        </script>
      </Helmet>
      
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Blog Header */}
            <Box sx={{ my: 4 }}>   
              {/* Breadcrumb navigation */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate('/')}
                    sx={{ 
                      fontWeight: 500,
                      minWidth: 'auto',
                      px: 1
                    }}
                  >
                    Home
                  </Button>
                  {' > '}
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate('/public/blogs')}
                    sx={{ 
                      fontWeight: 500,
                      minWidth: 'auto',
                      px: 1
                    }}
                  >
                    Articles
                  </Button>
                  {' > '}
                  <Typography component="span" sx={{ color: '#1e3c72', fontWeight: 500 }}>
                    Current Article
                  </Typography>
                </Typography>
              </Box>
              
              {/* Blog image */}
              {blog && getBlogImage() && (
                <Box 
                  sx={{ 
                    width: '100%',
                    mb: 4,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                >
                  <img
                    src={`/${getBlogImage()}`}
                    alt={blog.title || 'Blog image'}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </Box>
              )}
              
              {/* Blog title */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Typography 
                  variant="h1" 
                  gutterBottom
                  sx={{
                    background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    fontWeight: 700
                  }}
                >
                  {blog?.title}
                </Typography>
              </motion.div>
              
              {/* Author info */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'primary.main', 
                    mr: 2 
                  }}
                >
                  {blog?.persona_id?.charAt(0)?.toUpperCase() || 'A'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {PERSONA_DISPLAY_NAMES[blog?.persona_id] || blog?.persona_id || 'Author'}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 4 }} />
            </Box>
            
            {/* Blog Content */}
            <Paper
              elevation={2}
              sx={{
                p: 4,
                borderRadius: 2,
                mb: 6
              }}
            >
              {formatContent(blog?.content)}
            </Paper>
            
            {/* Sign-up CTA instead of comments */}
            <Box sx={{ mb: 6 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #e4ecf7 100%)'
                }}
              >
                <Typography variant="h5" gutterBottom color="primary.dark">
                  Have questions about this project?
                </Typography>
                <Typography variant="body1" paragraph>
                  Sign in to ask our AI experts and get personalized guidance for your DIY projects.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                    background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  Sign In / Join DIYMind
                </Button>
              </Paper>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default PublicBlogPostPage;